import { useEffect, useMemo, useRef, useState } from "react"

import { Flex, Skeleton, Spin } from "antd"
import { Chart as ChartJS, Filler } from "chart.js"
import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { debounce } from "../../../../Utils/utils"
import { healthOverviewChartOptions } from "../../../DashboardPage/components/chartsOptions"
import { getGradient, tooltipLineWithPoints } from "../../../DashboardPage/components/chartsPlagins"
import ChartLegend from "../ChartLegend/ChartLegend"

ChartJS.register(Filler)

const SERIES = {
  CURRENT: "current",
  CURRENT_WITHOUT_US: "current_without_us",
  PREVIOUS: "previous",
  PREVIOUS_WITHOUT_US: "previous_without_us"
}

const HealthOverviewChart = ({ overviewData }) => {
  const [activeSeries, setActiveSeries] = useState(() => new Set(Object.values(SERIES)))
  const isSeriesVisible = (id) => activeSeries.size === 0 || activeSeries.has(id)
  const overviewFetching = useSelector((state) => state.health.overviewFetching)

  const { overviewType } = useSelector((state) => state.health.filters)
  const theme = useSelector((state) => state.nav.theme)

  //for better resize chart if resize window
  const chartRef = useRef(null)

  const handleResize = debounce(() => {
    if (chartRef.current) {
      chartRef.current.resize()
    }
  }, 500)

  //for udate chart after changing browser tab if chart is unvisible
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) {
      chartRef.current.update()
    }
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      window.removeEventListener("resize", handleResize)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const chartOptions = useMemo(() => healthOverviewChartOptions(theme), [theme])

  const getChartData = (data) => {
    const mainLabels = data?.labels || []
    const comparison = data?.comparison
    const compLabels = comparison?.labels || []
    const compResult = comparison?.result

    const mainSet = new Set(mainLabels)
    const hasOverlap = compLabels.length > 0 && compLabels.some((l) => mainSet.has(l))

    const mapAtLabels = (targetLabels, srcLabels, srcArr) => {
      if (!srcArr) return srcArr
      const map = new Map(srcLabels.map((l, i) => [l, srcArr[i]]))
      return targetLabels.map((l) => (map.has(l) ? map.get(l) : null))
    }

    let labels
    let mainWithUs, mainWithoutUs, compWithUs, compWithoutUs
    let mainLabelsAligned, compLabelsAligned

    if (hasOverlap) {
      const union = Array.from(new Set([...mainLabels, ...compLabels])).sort()
      labels = union
      mainWithUs = mapAtLabels(labels, mainLabels, data?.result?.with_us)
      mainWithoutUs = mapAtLabels(labels, mainLabels, data?.result?.without_us)
      compWithUs = mapAtLabels(labels, compLabels, compResult?.with_us)
      compWithoutUs = mapAtLabels(labels, compLabels, compResult?.without_us)
      mainLabelsAligned = labels.map((l) => (mainSet.has(l) ? l : null))
      compLabelsAligned = labels
    } else {
      const compLonger = compLabels.length > mainLabels.length
      labels = compLonger ? compLabels : mainLabels
      mainWithUs = data?.result?.with_us
      mainWithoutUs = data?.result?.without_us
      compWithUs = compResult?.with_us
      compWithoutUs = compResult?.without_us
      mainLabelsAligned = labels.map((_, i) => (i < mainLabels.length ? mainLabels[i] : null))
      compLabelsAligned = labels.map((_, i) => (i < compLabels.length ? compLabels[i] : null))
    }

    const compPeriodLabel = data?.comparison_period?.label
    const mainResult = data?.result || {}
    const compResultFull = comparison?.result || {}

    const datasets = [
      {
        seriesId: SERIES.CURRENT,
        hidden: !isSeriesVisible(SERIES.CURRENT),
        label: `${overviewType}`,
        data: mainWithUs,
        selectType: overviewType,
        with_us: data?.result?.with_us,
        without_us: data?.result?.without_us,

        with_us_count: mainResult.with_us_count,
        without_us_count: mainResult.without_us_count,
        with_us_volume: mainResult.with_us_volume,
        without_us_volume: mainResult.without_us_volume,

        main_labels: mainLabelsAligned,
        comparison_labels: compLabelsAligned,
        comparison_label: compPeriodLabel,
        comparison_with_us: compResultFull.with_us,
        comparison_without_us: compResultFull.without_us,
        comparison_with_us_count: compResultFull.with_us_count,
        comparison_without_us_count: compResultFull.without_us_count,
        comparison_with_us_volume: compResultFull.with_us_volume,
        comparison_without_us_volume: compResultFull.without_us_volume,

        borderColor: "#225AEA",
        borderWidth: 2,
        yAxisID: "y",
        fill: true,
        backgroundColor: function (context) {
          const { ctx, chartArea } = context.chart
          if (!chartArea) return
          return getGradient(ctx, chartArea)
        },
        ...(labels.length === 1
          ? { pointHoverRadius: 6, pointRadius: 4 }
          : { pointHoverRadius: 0, pointRadius: 0 })
      },
      {
        seriesId: SERIES.CURRENT_WITHOUT_US,
        hidden: !isSeriesVisible(SERIES.CURRENT_WITHOUT_US),
        label: `${overviewType} without us`,
        data: mainWithoutUs,
        selectType: overviewType,
        with_us: data?.result?.with_us,
        without_us: data?.result?.without_us,

        borderColor: "#A1A1AA",
        borderWidth: 2,
        ...(labels.length === 1
          ? { pointHoverRadius: 6, pointRadius: 4 }
          : { pointHoverRadius: 0, pointRadius: 0 }),
        yAxisID: "y",
        borderDash: [8, 6]
      }
    ]

    if (compWithUs?.length) {
      datasets.push({
        seriesId: SERIES.PREVIOUS,
        hidden: !isSeriesVisible(SERIES.PREVIOUS),
        label: `${overviewType} previous`,
        data: compWithUs,
        borderColor: "#F79009",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
        yAxisID: "y",
        fill: false,
        order: 3
      })
    }

    if (compWithoutUs?.length) {
      datasets.push({
        seriesId: SERIES.PREVIOUS_WITHOUT_US,
        hidden: !isSeriesVisible(SERIES.PREVIOUS_WITHOUT_US),
        label: `${overviewType} without us previous`,
        data: compWithoutUs,
        borderColor: "#F79009",
        borderWidth: 1.5,
        borderDash: [8, 6],
        pointRadius: 0,
        pointHoverRadius: 0,
        yAxisID: "y",
        fill: false,
        order: 4
      })
    }

    return { labels, datasets }
  }

  const chartData = getChartData(overviewData)
  const hasCompare = !!overviewData?.comparison?.result

  const toggleSeries = (id) => {
    setActiveSeries((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const legendItems = useMemo(() => getLegendItems(hasCompare), [hasCompare])

  return (
    <Flex vertical>
      <Spin spinning={overviewFetching}>
        <Line
          ref={chartRef}
          options={chartOptions}
          data={chartData}
          height={300}
          plugins={[tooltipLineWithPoints]}
        />
      </Spin>
      {overviewFetching ? (
        <Flex gap={12} wrap style={{ marginTop: 12, padding: "0 12px" }}>
          {legendItems.map((item) => (
            <Skeleton.Button key={item.id} active size="small" style={{ width: 110, height: 32 }} />
          ))}
        </Flex>
      ) : (
        <ChartLegend
          items={legendItems}
          isActive={(id) => activeSeries.has(id)}
          onToggle={toggleSeries}
          style={{ marginTop: 12, padding: "0 12px" }}
        />
      )}
    </Flex>
  )
}

export default HealthOverviewChart

function getLegendItems(hasCompare) {
  const base = [
    {
      id: SERIES.CURRENT,
      color: "#225AEA",
      dashed: false,
      label: "With us"
    },
    {
      id: SERIES.CURRENT_WITHOUT_US,
      color: "#A1A1AA",
      dashed: true,
      label: "Without us"
    }
  ]

  if (hasCompare) {
    return [
      ...base,
      {
        id: SERIES.PREVIOUS,
        color: "#F79009",
        dashed: false,
        label: "Prev. with us"
      },
      {
        id: SERIES.PREVIOUS_WITHOUT_US,
        color: "#F79009",
        dashed: true,
        label: "Prev. without us"
      }
    ]
  }
  return base
}
